import {
	assignRoles,
	defensiveRoleScore,
	isPitcherCapable,
	pitcherSlots,
} from "./rosterComposition.baseball.ts";
import { idb } from "../../db/index.ts";
import { g, helpers, local } from "../../util/index.ts";
import type { Position } from "../../../common/types.baseball.ts";
import type { Player, PlayerFiltered } from "../../../common/types.ts";
import { groupByUnique, last, maxBy } from "../../../common/utils.ts";

const score = (p: PlayerFiltered, pos?: Position) => {
	if (pos === undefined) {
		return p.ratings.ovr;
	}

	let tempScore = p.ratings.ovrs[pos];

	if (p.ratings.pos === pos) {
		if (pos === "C") {
			// More likely to put actual catcher at catcher
			tempScore += 25;
		} else if (pos !== "RP") {
			// Relief pitcher, who cares about "right" position
			tempScore += 10;
		}
	}

	return tempScore;
};

const DEF_POSITIONS = ["C", "1B", "2B", "3B", "SS", "LF", "CF", "RF"] as const;
const DEF_POSITIONS_DH = [
	"C",
	"1B",
	"2B",
	"3B",
	"SS",
	"LF",
	"CF",
	"RF",
	"DH",
] as const;
const NUM_STARTERS = 5;

const sortBattingOrder = (
	starters: {
		i: number;
		p: {
			ratings: {
				con: number;
				hpw: number;
				eye: number;
				spd: number;
			};
		};
	}[],
) => {
	const hasDh = starters.length === 9;

	const availablePlayers = new Set(starters);

	const lineup = [];

	// Helper function: scores players using all 4 ratings with custom weights
	const pluckBestPlayer = (weights: {
		con: number;
		hpw: number;
		eye: number;
		spd: number;
	}) => {
		const p = maxBy(
			availablePlayers,
			({ p }) =>
				p.ratings.con * weights.con +
				p.ratings.hpw * weights.hpw +
				p.ratings.eye * weights.eye +
				p.ratings.spd * weights.spd,
		);
		if (p) {
			availablePlayers.delete(p);
		}

		return p;
	};

	const bestHitter = { con: 1, hpw: 1, eye: 1, spd: 0.2 };

	// Best hitters 2nd and 3rd
	lineup[1] = pluckBestPlayer(bestHitter);
	lineup[2] = pluckBestPlayer(bestHitter);

	// Next best hitter 1st, but also value speed
	lineup[0] = pluckBestPlayer({ con: 1, hpw: 1, eye: 1, spd: 1 });

	// Best remaining power hitters 4th and 5th
	lineup[3] = pluckBestPlayer({ con: 0.5, hpw: 1, eye: 0.5, spd: 0.1 });
	lineup[4] = pluckBestPlayer({ con: 0.5, hpw: 1, eye: 0.5, spd: 0.1 });

	// If we have a DH, then make 9th "second leadoff". Otherwise, put them 6th
	const secondLeadoff = pluckBestPlayer({ con: 1, hpw: 0.25, eye: 1, spd: 1 });
	if (hasDh) {
		lineup[5] = pluckBestPlayer(bestHitter);
		lineup[6] = pluckBestPlayer(bestHitter);
		lineup[7] = pluckBestPlayer(bestHitter);
		lineup[8] = secondLeadoff;
	} else {
		lineup[5] = secondLeadoff;
		lineup[6] = pluckBestPlayer(bestHitter);
		lineup[7] = pluckBestPlayer(bestHitter);
	}

	return lineup.filter((x) => x !== undefined);
};

export const getDepthDefense = (
	players: {
		pid: number;
		ratings: {
			ovrs: Record<string, number>;
			pos: string;
		};
	}[],
	dh: boolean,
) => {
	const defPositions = dh ? DEF_POSITIONS_DH : DEF_POSITIONS;
	const assignment = assignRoles(
		defPositions.map((pos) =>
			players.map((p) => defensiveRoleScore(p.ratings, pos)),
		),
	);
	const chosen = new Set(assignment.filter((i) => i >= 0));
	const defensivePlayersSorted = assignment
		.filter((i) => i >= 0)
		.map((i) => players[i]!);
	const playersRemaining = players.filter((_, i) => !chosen.has(i));

	playersRemaining.sort((a, b) => {
		const bOvr =
			b.ratings.pos === "RP" || b.ratings.pos === "SP"
				? b.ratings.ovrs.LF!
				: b.ratings.ovrs[b.ratings.pos]!;
		const aOvr =
			a.ratings.pos === "RP" || a.ratings.pos === "SP"
				? a.ratings.ovrs.LF!
				: a.ratings.ovrs[a.ratings.pos]!;
		const diff = bOvr - aOvr;
		if (diff === 0) {
			// Deterministic order
			return b.pid - a.pid;
		}
		return diff;
	});
	defensivePlayersSorted.push(...playersRemaining);

	return defensivePlayersSorted.map((p) => p.pid);
};

const byRP = (
	a: { pid: number; ratings: { ovrs: Record<string, number> } },
	b: { pid: number; ratings: { ovrs: Record<string, number> } },
) => {
	const diff = b.ratings.ovrs.RP! - a.ratings.ovrs.RP!;
	if (diff === 0) {
		// Deterministic order
		return b.pid - a.pid;
	}
	return diff;
};

export const getDepthPitchers = <
	T extends {
		pid: number;
		ratings: {
			ovrs: Record<string, number>;
			pos?: string;
		};
	},
>(
	players: T[],
) => {
	// Rotation, closer and setup men are chosen together, so a pitcher goes where he adds
	// the most: a 61 SP/61 RP starts over a 58 SP/47 RP, while the best pure reliever closes.
	const pitchers = players.filter((p) =>
		isPitcherCapable({ pos: p.ratings.pos ?? "", ovrs: p.ratings.ovrs }),
	);
	const others = players.filter((p) => !pitchers.includes(p));
	const slots = pitcherSlots();
	const assignment = assignRoles(
		slots.map((slot) =>
			pitchers.map((p) => slot.weight * (p.ratings.ovrs[slot.pos] ?? 0)),
		),
	);
	const pick = (i: number | undefined) =>
		i !== undefined && i >= 0 ? pitchers[i] : undefined;

	const rotation = assignment
		.slice(0, NUM_STARTERS)
		.map(pick)
		.filter((p) => p !== undefined)
		.sort((a, b) => score(b, "SP") - score(a, "SP") || b.pid - a.pid);
	const closer = pick(assignment[NUM_STARTERS]);
	const used = new Set<T>([...rotation, ...(closer ? [closer] : [])]);
	const bullpen = pitchers.filter((p) => !used.has(p)).sort(byRP);
	const rest = [...others].sort(byRP);

	// Short of real starters, the best remaining arms fill the rotation before the closer
	while (rotation.length < NUM_STARTERS) {
		const pool = bullpen.length > 0 ? bullpen : rest;
		const next = maxBy(pool, (p) => score(p, "SP"));
		if (!next) {
			break;
		}
		pool.splice(pool.indexOf(next), 1);
		rotation.push(next);
	}

	return [
		...rotation,
		...(closer ? [closer] : []),
		...bullpen,
		...rest,
	].map((p) => p.pid);
};

const genDepth = async (
	playersRaw: Player[],
	initialDepth: {
		L: number[];
		LP: number[];
		D: number[];
		DP: number[];
		P: number[];
	},
	onlyNewPlayers?: boolean,
	pos?: "L" | "LP" | "D" | "DP" | "P",
) => {
	if (initialDepth === undefined) {
		throw new Error("Missing depth");
	}
	const depth = helpers.deepCopy(initialDepth);

	let players: any[];

	// Can't use getCopies in exhibition game, and also want to ignore fuzz, so just keep these two code paths
	if (local.exhibitionGamePlayers) {
		players = playersRaw.map((p) => {
			const ratings = last(p.ratings);
			return {
				pid: p.pid,
				ratings: {
					spd: ratings.spd,
					cat: (ratings as { cat?: number }).cat,
					pos: ratings.pos,
					ovrs: ratings.ovrs,
				},
			};
		});
	} else {
		// No stats are read here. showNoStats/showRookies would only make playersPlus deep copy every player's stats
		// rows for nothing: with no stats requested, players with ratings for the season are returned either way.
		players = await idb.getCopies.playersPlus(playersRaw, {
			attrs: ["pid"],
			ratings: ["spd", "con", "hpw", "eye", "cat", "pos", "ovrs"],
			season: g.get("season"),
			fuzz: true,
		});
	}

	// Lineup last, since that depends on defensive starters
	const positions: (keyof typeof depth)[] = pos
		? [pos]
		: ["P", "D", "DP", "L", "LP"];

	for (const pos2 of positions) {
		if (onlyNewPlayers) {
			if (pos2 === "L" || pos2 === "LP") {
				continue;
			}

			// Identify players not currently in the depth chart, and add them to the depth chart above any player worse
			// than them without otherwise disturbing the order of the depth chart. This is useful for adding free agents to
			// the user's team - start them if they're better, but otherwise don't fuck with the user's depth chart.
			const playersNotInDepth = players.filter(
				(p) => !depth[pos2].includes(p.pid),
			);

			if (pos2 === "D" || pos2 === "DP") {
				const addToBench = [];

				const defPositions = pos2 === "D" ? DEF_POSITIONS_DH : DEF_POSITIONS;

				for (const p of playersNotInDepth) {
					let added = false;

					// Put in starting lineup if better than existing starter
					for (const [i, scorePos] of defPositions.entries()) {
						const pScore = score(p, scorePos);
						const p2 = players.find((p2) => p2.pid === depth[pos2][i]);
						if (!p2 || pScore > score(p2, scorePos)) {
							depth[pos2][i] = p.pid;
							added = true;

							if (p2) {
								addToBench.push(p2);
							}

							break;
						}
					}

					if (!added) {
						addToBench.push(p);
					}
				}

				// Add any players not put in starting lineup or removed from starting lineup to bench
				for (const p of addToBench) {
					const pScore = score(p);
					let added = false;
					for (let i = defPositions.length; i < depth[pos2].length; i++) {
						const p2 = players.find((p2) => p2.pid === depth[pos2][i]);

						if (!p2 || pScore > score(p2)) {
							depth[pos2].splice(i, 0, p.pid);
							added = true;
							break;
						}
					}

					if (!added) {
						depth[pos2].push(p.pid);
					}
				}
			} else if (pos2 === "P") {
				const addToBullpen = [];

				for (const p of playersNotInDepth) {
					let added = false;

					// Put in starting rotation if better than existing starter
					for (let i = 0; i < NUM_STARTERS; i++) {
						const pScore = score(p, "SP");
						const p2 = players.find((p2) => p2.pid === depth[pos2][i]);
						if (!p2 || pScore > score(p2, "SP")) {
							depth[pos2].splice(i, 0, p.pid);
							added = true;

							// Move last starter to reliever
							const lastStarter = players.find(
								(p2) => p2.pid === depth[pos2][NUM_STARTERS],
							);
							if (lastStarter) {
								depth[pos2].splice(NUM_STARTERS, 1);
								addToBullpen.push(lastStarter);
							}

							break;
						}
					}

					if (!added) {
						addToBullpen.push(p);
					}
				}

				// Add any players not put in starting lineup or removed from starting lineup to bullpen
				for (const p of addToBullpen) {
					const pScore = score(p, "RP");
					let added = false;
					for (let i = NUM_STARTERS; i < depth[pos2].length; i++) {
						const p2 = players.find((p2) => p2.pid === depth[pos2][i]);

						if (!p2 || pScore > score(p2, "RP")) {
							depth[pos2].splice(i, 0, p.pid);
							added = true;
							break;
						}
					}

					if (!added) {
						depth[pos2].push(p.pid);
					}
				}
			}
		} else {
			depth[pos2] = [];

			if (pos2 === "LP" || pos2 === "L") {
				const depthDefense = depth[pos2 === "L" ? "D" : "DP"];
				const playersByPid = groupByUnique(players, "pid");

				const starters = depthDefense
					.slice(0, pos2 === "L" ? 9 : 8)
					.map((pid, i) => ({
						i,
						p: playersByPid[pid],
					}));

				const sortedStarters = sortBattingOrder(starters);

				const indexes = sortedStarters.map((info) => info.i);

				if (pos2 === "LP") {
					// Pitcher last
					indexes.push(-1);
				}

				depth[pos2] = indexes;
			} else if (pos2 === "DP") {
				depth[pos2] = getDepthDefense(players, false);
			} else if (pos2 === "D") {
				depth[pos2] = getDepthDefense(players, true);
			} else if (pos2 === "P") {
				depth[pos2] = getDepthPitchers(players);
			}
		}
	}

	return depth;
};

export default genDepth;
